import React, { useState, useEffect } from 'react';
import { RunResult, SessionMessage, SessionInfo, RegistryEntryResponse, StepType } from '../../types';
import { Bot, MessageSquare, GitBranch, Play, CheckCircle, ChevronLeft, ChevronRight, Lightbulb, Edit3, Settings, Archive, Inbox, Layers } from 'lucide-react';
import FormattedOutputDisplay from './FormattedOutputDisplay';
import EnhancedFormattedDisplay from './EnhancedFormattedDisplay';
import RunResultErrorPanel from './RunResultErrorPanel';
import ToolResultsDisplay from './ToolResultsDisplay';
import QueueReadOutputRenderer, { isQueueReadOutput } from './QueueReadOutputRenderer';
import QueueWriteOutputRenderer, { isQueueWriteOutput } from './QueueWriteOutputRenderer';
import { isToolResult, parseToolResult } from '../../types/toolResults';
import apiService from '../../services/api';
import { getStepTypeConfig } from '../../utils/stepTypeConfig';
import Tooltip from '../common/Tooltip';

interface OutputDisplayModalProps {
  isOpen: boolean;
  onClose: () => void;
  runResult: RunResult | null;
  title?: string;
  workflowSteps?: Array<{
    orchestration_step_id: string | number;
    step_type: string;
    description?: string;
    label?: string;
    agent_id?: number;
    config?: Record<string, any>;
  }>;
  allRunResults: RunResult[];
  onNavigateToStep?: (runResult: RunResult) => void;
  sessionMessages?: SessionMessage[];
  sessionInfoMap?: Record<number, SessionInfo>;
  agents?: Array<{ agent_id: number; name: string }>;
  onEditAgent?: (agentId: number, focusField: 'model') => void;
}

// Helper to get queue information from step config (matches InlineOutputDisplay)
const getQueueInfo = (step: { step_type: string; config?: Record<string, any> } | undefined) => {
  if (!step) return null;

  if (step.step_type === 'queue_write') {
    const queueId = step.config?.target_queue_id;
    const queueName = step.config?.target_queue_name;
    return { queueId, queueName, type: 'write' as const };
  } else if (step.step_type === 'queue_read') {
    const queueId = step.config?.source_queue_id;
    const queueName = step.config?.source_queue_name;
    return { queueId, queueName, type: 'read' as const };
  }
  return null;
};

// Helper to reconstruct the input for a step based on backend logic
function getStepInput(runResult: RunResult, allRunResults: RunResult[], workflowSteps: Array<{ orchestration_step_id: string | number; step_type: string; description?: string }>): any {
  if (!runResult) return null;
  const inputType = runResult.input_type;
  
  if (inputType === 'input_message') {
    // Use input_message from the current runResult
    return runResult.input_message || 'No input message available.';
  }
  
  // Check if current step has enhanced input with feedback (stored in current step's as_input_list_items)
  if (inputType === 'input_list' && Array.isArray(runResult.as_input_list_items) && runResult.as_input_list_items.length > 0) {
    // Check if this contains feedback (look for feedback patterns in the content)
    const hasFeedback = runResult.as_input_list_items.some(item => 
      item.item_data && 
      typeof item.item_data === 'object' && 
      item.item_data.content && 
      typeof item.item_data.content === 'string' &&
      item.item_data.content.includes('Feedback (iteration')
    );
    
    if (hasFeedback) {
      // This step has feedback-enhanced input, return it directly
      return runResult.as_input_list_items.map(item => item.item_data);
    }
  }
  
  // NEW: Use input_run_result_id if available (precise reference)
  if ((inputType === 'input_list' || inputType === 'final_output' || inputType === 'model') && runResult.input_run_result_id) {
    // Find the exact RunResult that provided the input
    const inputRunResult = allRunResults.find(r => String(r.run_result_id) === String(runResult.input_run_result_id));
    if (inputRunResult) {
      if (inputType === 'input_list' && Array.isArray(inputRunResult.as_input_list_items) && inputRunResult.as_input_list_items.length > 0) {
        return inputRunResult.as_input_list_items.map(item => item.item_data);
      }
      // For final_output/model or fallback for input_list
      if (inputRunResult.final_output) {
        // Check if this is a fan-out result (contains fan_out_results field)
        if (typeof inputRunResult.final_output === 'object' && 
            inputRunResult.final_output !== null && 
            !Array.isArray(inputRunResult.final_output) &&
            'fan_out_results' in (inputRunResult.final_output as any)) {
          // Extract fan_out_results and convert to the format expected by the backend
          const fanOutResults = (inputRunResult.final_output as any).fan_out_results || [];
          return fanOutResults.map((result: any, index: number) => ({
            content: String(result),
            role: 'user'
          }));
        }
        return inputRunResult.final_output;
      }
      return 'Input run result has no output.';
    }
    return 'Referenced input run result not found.';
  }
  
  // FALLBACK: Use input_step_id for backward compatibility
  if ((inputType === 'input_list' || inputType === 'final_output' || inputType === 'model') && runResult.input_step_id) {
    // Find the previous step's runResult (this may be ambiguous in feedback loops)
    const prev = allRunResults.find(r => String(r.orchestration_step_id) === String(runResult.input_step_id));
    if (!prev) return 'Referenced input step not found.';
    if (inputType === 'input_list' && Array.isArray(prev.as_input_list_items) && prev.as_input_list_items.length > 0) {
      return prev.as_input_list_items.map(item => item.item_data);
    }
    // For final_output/model or fallback for input_list
    if (prev.final_output) {
      // Check if this is a fan-out result (contains fan_out_results field)
      if (typeof prev.final_output === 'object' && 
          prev.final_output !== null && 
          !Array.isArray(prev.final_output) &&
          'fan_out_results' in (prev.final_output as any)) {
        // Extract fan_out_results and convert to the format expected by the backend
        const fanOutResults = (prev.final_output as any).fan_out_results || [];
        return fanOutResults.map((result: any, index: number) => ({
          content: String(result),
          role: 'user'
        }));
      }
      return prev.final_output;
    }
    return 'Previous step has no output.';
  }
  return 'Input type not recognized or insufficient data.';
}

const OutputDisplayModal: React.FC<OutputDisplayModalProps> = ({
  isOpen,
  onClose,
  runResult,
  title = 'Step Output Details',
  workflowSteps = [],
  allRunResults,
  onNavigateToStep,
  sessionMessages = [],
  sessionInfoMap = {},
  agents = [],
  onEditAgent,
}) => {
  const [activeTab, setActiveTab] = useState<'output' | 'input' | 'tools' | 'metadata' | 'hints' | 'instructions' | 'session' | 'registry'>('output');

  // AC-388: Registry entries state
  const [registryEntries, setRegistryEntries] = useState<RegistryEntryResponse[]>([]);
  const [registryLoading, setRegistryLoading] = useState(false);
  const [registryTotal, setRegistryTotal] = useState(0);

  // AC-388: Fetch registry entries when modal opens
  useEffect(() => {
    if (isOpen && runResult?.run_instance_id) {
      const fetchRegistryEntries = async () => {
        setRegistryLoading(true);
        try {
          const response = await apiService.getRegistryEntriesByRun(
            parseInt(runResult.run_instance_id, 10)
          );
          setRegistryEntries(response.entries);
          setRegistryTotal(response.total);
        } catch (error) {
          console.error('Error fetching registry entries:', error);
          setRegistryEntries([]);
          setRegistryTotal(0);
        } finally {
          setRegistryLoading(false);
        }
      };
      fetchRegistryEntries();
    }
  }, [isOpen, runResult?.run_instance_id]);

  // Navigation logic
  const getNavigationSteps = () => {
    if (!allRunResults || allRunResults.length === 0 || !runResult) {
      return { previousStep: null, nextStep: null };
    }

    // Sort run results by step_order to ensure proper navigation
    const sortedResults = [...allRunResults].sort((a, b) => (a.step_order || 0) - (b.step_order || 0));
    // Use String() conversion for comparison to handle type mismatches (AC-502)
    const currentIndex = sortedResults.findIndex(result => String(result.run_result_id) === String(runResult.run_result_id));
    
    if (currentIndex === -1) {
      return { previousStep: null, nextStep: null };
    }

    const previousStep = currentIndex > 0 ? sortedResults[currentIndex - 1] : null;
    const nextStep = currentIndex < sortedResults.length - 1 ? sortedResults[currentIndex + 1] : null;

    return { previousStep, nextStep };
  };

  const { previousStep, nextStep } = getNavigationSteps();

  const handleNavigateToStep = React.useCallback((targetStep: RunResult) => {
    if (onNavigateToStep) {
      onNavigateToStep(targetStep);
    }
  }, [onNavigateToStep]);

  // Keyboard navigation
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;
      
      switch (event.key) {
        case 'ArrowLeft':
          if (previousStep) {
            event.preventDefault();
            handleNavigateToStep(previousStep);
          }
          break;
        case 'ArrowRight':
          if (nextStep) {
            event.preventDefault();
            handleNavigateToStep(nextStep);
          }
          break;
        case 'Escape':
          event.preventDefault();
          onClose();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, previousStep, nextStep, onClose, handleNavigateToStep]);

  // State for fetched queue name (fallback for backward compatibility)
  const [fetchedQueueName, setFetchedQueueName] = useState<string | null>(null);
  const [isFetchingQueueName, setIsFetchingQueueName] = useState(false);

  // Get step, queue, and agent information
  const currentStep = runResult ? workflowSteps.find(s => String(s.orchestration_step_id) === String(runResult.orchestration_step_id)) : undefined;
  const queueInfo = currentStep ? getQueueInfo(currentStep) : null;
  const stepTypeInfo = currentStep?.step_type ? getStepTypeConfig(currentStep.step_type as StepType) : null;
  const agent = currentStep?.agent_id ? agents.find(a => a.agent_id === currentStep.agent_id) : null;

  // Fetch queue name from API if not in config (backward compatibility)
  useEffect(() => {
    if (queueInfo?.queueId && !queueInfo.queueName && !fetchedQueueName && !isFetchingQueueName) {
      setIsFetchingQueueName(true);
      apiService.getQueue(queueInfo.queueId)
        .then(queue => {
          setFetchedQueueName(queue.name);
        })
        .catch(error => {
          console.error('Failed to fetch queue name:', error);
        })
        .finally(() => {
          setIsFetchingQueueName(false);
        });
    }
  }, [queueInfo?.queueId, queueInfo?.queueName, fetchedQueueName, isFetchingQueueName]);

  // Reset fetched name when queue info changes
  useEffect(() => {
    setFetchedQueueName(null);
  }, [queueInfo?.queueId]);

  // Tool Usage: extract tool calls from run result items and session messages.
  // NOTE: this useMemo must run unconditionally on every render - do NOT move it
  // below the `if (!isOpen || !runResult) return null` early return or React's
  // hook order will change between renders and crash the modal.
  const currentStepToolCalls = React.useMemo(() => {
    const calls: Array<{
      id: string;
      name: string;
      arguments: Record<string, any>;
      result?: string;
      timestamp?: string;
    }> = [];

    // Source 1: as_input_list_items - OpenAI Responses-API shape, synthesized
    // for all runs post-AC-1557 unification. Pre-unification this was the
    // OpenAI Agents SDK's native shape, written directly by Runner.run via
    // result.to_input_list(). Post-unification, the Pydantic AI runtime adapter
    // synthesizes the SAME shape from ``result.all_messages()`` via
    // ``_synthesize_pai_input_list_items`` (AC-1559), so this remains the
    // primary tool-call source for new runs regardless of provider.
    // Tool calls have type "function_call", results have type "function_call_output".
    const items = runResult?.as_input_list_items;
    if (items && Array.isArray(items)) {
      const functionCalls = items.filter(i => i.item_data?.type === 'function_call');
      const functionOutputs = items.filter(i => i.item_data?.type === 'function_call_output');

      for (const item of functionCalls) {
        const data = item.item_data;
        const callId = data.call_id || data.id || `call_${calls.length}`;
        let args: Record<string, any> = {};
        try {
          args = typeof data.arguments === 'string' ? JSON.parse(data.arguments) : (data.arguments || {});
        } catch {
          args = { _raw: data.arguments };
        }

        // Match output by call_id
        const output = functionOutputs.find(o => o.item_data?.call_id === callId);

        calls.push({
          id: callId,
          name: data.name || 'unknown',
          arguments: args,
          result: output?.item_data?.output,
          timestamp: runResult?.started_at,
        });
      }
    }

    // Source 2: sessionMessages (Chat Completions format) — only if no calls found above
    if (calls.length === 0) {
      const currentSessionId = runResult?.session_id;
      if (currentSessionId && sessionMessages?.length) {
        const msgs = sessionMessages.filter(m => m.session_id === currentSessionId);
        const toolResultMessages = msgs.filter(m => m.role === 'tool');

        for (const msg of msgs) {
          if (!msg.tool_calls || !Array.isArray(msg.tool_calls)) continue;
          for (const tc of msg.tool_calls) {
            const fnName = tc.function?.name || tc.name || 'unknown';
            let args: Record<string, any> = {};
            try {
              const rawArgs = tc.function?.arguments || tc.arguments;
              args = typeof rawArgs === 'string' ? JSON.parse(rawArgs) : (rawArgs || {});
            } catch {
              args = { _raw: tc.function?.arguments || tc.arguments };
            }

            const resultMsg = toolResultMessages.find(
              rm => rm.content_json?.tool_call_id === tc.id ||
                    (rm.message_order === msg.message_order + 1 && rm.role === 'tool')
            );

            calls.push({
              id: tc.id || `call_${calls.length}`,
              name: fnName,
              arguments: args,
              result: resultMsg?.content,
              timestamp: msg.created_at,
            });
          }
        }
      }
    }

    // Source 3 (AC-1555): Pydantic AI ToolCallPart shape, fallback for
    // historical PAI sessions written before AC-1559 synthesis was wired up.
    // Each SessionMessage row carries the full serialized ModelMessage in
    // ``content_json`` with a ``parts[]`` array. Tool calls live as
    // ``part_kind='tool-call'`` parts inside response messages; their returns
    // live as ``part_kind='tool-return'`` parts inside request messages,
    // linked by ``tool_call_id``.
    //
    // Gated on ``calls.length === 0`` so that source 1 (synthesized
    // OpenAI-Responses shape, the primary path for all new runs post-AC-1557)
    // and source 2 (legacy Chat Completions sessions) continue to use their
    // more specific matchers. This branch only fires for older PAI session
    // data written between AC-1554 (session writeback turned on) and AC-1559
    // (input-list synthesis turned on).
    if (calls.length === 0) {
      const currentSessionId = runResult?.session_id;
      if (currentSessionId && sessionMessages?.length) {
        const msgs = sessionMessages.filter(m => m.session_id === currentSessionId);

        // First pass: collect all tool-return parts indexed by tool_call_id
        // so we can attach them as ``result`` to the matching tool-call.
        const returnsByCallId = new Map<string, string>();
        for (const msg of msgs) {
          const parts = msg.content_json?.parts;
          if (!parts || !Array.isArray(parts)) continue;
          for (const part of parts) {
            if (part?.part_kind === 'tool-return' && part.tool_call_id) {
              const content = part.content;
              const contentStr =
                typeof content === 'string'
                  ? content
                  : (content == null ? '' : JSON.stringify(content));
              returnsByCallId.set(String(part.tool_call_id), contentStr);
            }
          }
        }

        // Second pass: collect tool-call parts and attach their returns.
        for (const msg of msgs) {
          const parts = msg.content_json?.parts;
          if (!parts || !Array.isArray(parts)) continue;
          for (const part of parts) {
            if (part?.part_kind !== 'tool-call') continue;
            const callId = part.tool_call_id
              ? String(part.tool_call_id)
              : `call_${calls.length}`;
            // ``args`` on a tool-call part can be either a parsed object
            // (model returned structured args) or a JSON string (model
            // returned raw arguments). Mirror the source-1 fallback.
            let args: Record<string, any> = {};
            const rawArgs = part.args;
            try {
              args =
                typeof rawArgs === 'string'
                  ? JSON.parse(rawArgs)
                  : (rawArgs || {});
            } catch {
              args = { _raw: rawArgs };
            }

            calls.push({
              id: callId,
              name: part.tool_name || 'unknown',
              arguments: args,
              result: returnsByCallId.get(callId),
              timestamp: msg.created_at,
            });
          }
        }
      }
    }

    return calls;
  }, [runResult?.as_input_list_items, runResult?.session_id, sessionMessages]);

  // Get queue display name
  const getQueueDisplayName = () => {
    if (!queueInfo) return null;
    if (queueInfo.queueName) return queueInfo.queueName;
    if (fetchedQueueName) return fetchedQueueName;
    if (queueInfo.queueId) return `Queue #${queueInfo.queueId}`;
    return null;
  };
  const queueDisplayName = getQueueDisplayName();

  if (!isOpen || !runResult) return null;

  // Use Layers icon for queue steps (matches InlineOutputDisplay), otherwise use icon from config
  const isQueueStep = currentStep?.step_type === 'queue_write' || currentStep?.step_type === 'queue_read';
  const StepIcon = isQueueStep ? Layers : (stepTypeInfo?.icon || Bot);
  const stepTypeName = stepTypeInfo?.name || 'Unknown';

  // Extract text color from stepTypeInfo.color (format: "bg-indigo-50 text-indigo-700")
  const getTextColor = () => {
    if (!stepTypeInfo?.color) return 'text-gray-600 dark:text-gray-400';
    const colorParts = stepTypeInfo.color.split(' ');
    const textColor = colorParts.find(part => part.startsWith('text-'));
    return textColor || 'text-gray-600 dark:text-gray-400';
  };
  const stepTypeColor = getTextColor();

  const formatTimestamp = (timestamp: string | null | undefined) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return isNaN(date.getTime()) ? 'N/A' : date.toLocaleString();
  };

  const formatDuration = () => {
    // AC-1580: deterministic steps (filter, rdap) historically didn't
    // populate started_at on the RunResult row. ``new Date(null).getTime()``
    // returns 0 (Unix epoch), so a missing started_at made the duration
    // display as the entire Unix timestamp (~56 years). Guard against
    // null/undefined explicitly — only the formatted string is computed
    // when BOTH timestamps are present and parse cleanly.
    if (!runResult.completed_at || !runResult.started_at) return 'N/A';
    const start = new Date(runResult.started_at).getTime();
    const end = new Date(runResult.completed_at).getTime();
    if (Number.isNaN(start) || Number.isNaN(end)) return 'N/A';
    return ((end - start) / 1000).toFixed(2) + 's';
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
  };

  const exportOutput = () => {
    const data = {
      stepId: runResult.orchestration_step_id,
      stepOrder: runResult.step_order,
      status: runResult.status,
      output: runResult.final_output,
      error: runResult.error_message,
      startTime: runResult.started_at,
      endTime: runResult.completed_at,
      inputData: runResult.input_data,
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `step-${runResult.step_order}-output.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const renderOutputContent = () => {
    if (!runResult.final_output) {
      if (runResult.error_message) {
        return (
          <RunResultErrorPanel runResult={runResult} onEditAgent={onEditAgent} />
        );
      }
      return (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p>No output available for this step</p>
        </div>
      );
    }

    // PRIORITY 1: Check if this is a tool result (GoDaddy, Stripe, etc.)
    // Tool results get custom visualizations before falling back to generic displays
    const parsedToolResult = parseToolResult(runResult.final_output);
    if (parsedToolResult) {
      return (
        <ToolResultsDisplay
          data={runResult.final_output}
          onCopy={copyToClipboard}
        />
      );
    }

    // PRIORITY 1.5: Check if this is a queue step with structured output
    if (currentStep?.step_type === 'queue_read' && isQueueReadOutput(runResult.final_output)) {
      return <QueueReadOutputRenderer output={runResult.final_output} onCopy={copyToClipboard} />;
    }

    if (currentStep?.step_type === 'queue_write' && isQueueWriteOutput(runResult.final_output)) {
      return <QueueWriteOutputRenderer output={runResult.final_output} onCopy={copyToClipboard} />;
    }

    // PRIORITY 2: Check if this is a parallel fan-out result with summary fields
    const isFanOutResult = typeof runResult.final_output === 'object' &&
                          runResult.final_output !== null &&
                          !Array.isArray(runResult.final_output) &&
                          'total_items' in runResult.final_output &&
                          'successful_items' in runResult.final_output &&
                          'failed_items' in runResult.final_output;

    // Type assertion for fan-out result fields
    const fanOutData = runResult.final_output as any;

    return (
      <div className="space-y-6">
        {/* Fan-out Summary Section */}
        {isFanOutResult && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-primary-200 dark:border-blue-800 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mr-2">
                  <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-warm-white">Parallel Execution Summary</h3>
              </div>
              
              {/* Success Rate - moved to header */}
              {fanOutData.total_items > 0 && (
                <div className="flex items-center space-x-3">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Success Rate</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-warm-white">
                    {Math.round((fanOutData.successful_items / fanOutData.total_items) * 100)}%
                  </span>
                  <div className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                    <div 
                      className="bg-green-500 h-1.5 rounded-full transition-all duration-300"
                      style={{ 
                        width: `${(fanOutData.successful_items / fanOutData.total_items) * 100}%` 
                      }}
                    ></div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white dark:bg-cap-dark rounded-lg p-3 border border-blue-100 dark:border-blue-800">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Total</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-warm-white">{fanOutData.total_items}</p>
                  </div>
                  <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                </div>
              </div>
              
              <div className="bg-white dark:bg-cap-dark rounded-lg p-3 border border-green-100 dark:border-green-800">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Success</p>
                    <p className="text-lg font-bold text-green-600">{fanOutData.successful_items}</p>
                  </div>
                  <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
              </div>
              
              <div className="bg-white dark:bg-cap-dark rounded-lg p-3 border border-red-100 dark:border-red-800">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Failed</p>
                    <p className="text-lg font-bold text-red-600">{fanOutData.failed_items}</p>
                  </div>
                  <div className="w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

                 {/* Detailed Results Section */}
         <div>
           {/* Check if this is a feedback loop result */}
           {typeof runResult.final_output === 'object' && 
            runResult.final_output !== null && 
            !Array.isArray(runResult.final_output) &&
            (runResult.final_output as any).action === 'feedback_loop' ? (
             /* Special handling for feedback loop results */
             <EnhancedFormattedDisplay
               data={runResult.final_output}
               title="Feedback Loop Result"
               showToggle={true}
               maxHeight="max-h-96"
               className=""
               initiallyExpanded={true}
               displayType="output"
               workflowSteps={workflowSteps}
             />
           ) : typeof runResult.final_output === 'object' && runResult.final_output !== null && !Array.isArray(runResult.final_output) ? (
            /* If final_output is an object, render each key/value with FormattedOutputDisplay */
            <div className="space-y-4">
              {Object.entries(runResult.final_output)
                .filter(([key]) => !['total_items', 'successful_items', 'failed_items'].includes(key))
                .map(([key, value]) => (
                  <div key={key} className="bg-white dark:bg-cap-dark border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <EnhancedFormattedDisplay 
                      data={value} 
                      initiallyExpanded={true}
                      title={key}
                      displayType="output"
                    />
                  </div>
                ))}
            </div>
          ) : (
            /* Otherwise, render as before */
            <EnhancedFormattedDisplay
              data={runResult.final_output}
              title="Step Output"
              showToggle={true}
              maxHeight="max-h-96"
              className=""
              initiallyExpanded={true}
              displayType="output"
            />
          )}

          {/* Image Attachments from MCP tool results (e.g., browser screenshots) */}
          {runResult.image_attachments && runResult.image_attachments.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Screenshots ({runResult.image_attachments.length})
              </h4>
              <div className="space-y-3">
                {runResult.image_attachments.map((img, idx) => (
                  <div key={idx} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                    {img.tool_name && (
                      <div className="px-3 py-1.5 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
                        {img.server_name ? `${img.server_name} / ` : ''}{img.tool_name}
                      </div>
                    )}
                    <img
                      src={`data:${img.mimeType || 'image/png'};base64,${img.data}`}
                      alt={`Screenshot ${idx + 1}${img.tool_name ? ` from ${img.tool_name}` : ''}`}
                      className="w-full h-auto"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderInputContent = () => {
    if (!runResult) return null;
    const input = getStepInput(runResult, allRunResults, workflowSteps);
    if (!input || (typeof input === 'string' && input.startsWith('No input'))) {
      return (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <p>{typeof input === 'string' ? input : 'No input data available for this step'}</p>
        </div>
      );
    }
    
    // Check if input is an object with array-like structure that should be displayed as individual items
    const shouldDisplayAsItems = (data: any): boolean => {
      if (Array.isArray(data)) return true;
      if (typeof data === 'object' && data !== null) {
        // Check if it has a single key with an array value (like {"searches": [...]})
        const keys = Object.keys(data);
        if (keys.length === 1) {
          const value = data[keys[0]];
          return Array.isArray(value) && value.length > 0;
        }
      }
      return false;
    };

    // If input should be displayed as individual items, extract the array
    let displayData = input;
    if (shouldDisplayAsItems(input)) {
      if (Array.isArray(input)) {
        displayData = input;
      } else {
        // Extract the array from the object (e.g., {"searches": [...]} -> [...]})
        const keys = Object.keys(input);
        if (keys.length === 1) {
          displayData = input[keys[0]];
        }
      }
    }
    
    return (
      <EnhancedFormattedDisplay
        data={displayData}
        title="Input Data"
        showToggle={true}
        maxHeight="max-h-96"
        className=""
        initiallyExpanded={true}
        displayType="input"
      />
    );
  };

  const renderToolUsageContent = () => {
    if (currentStepToolCalls.length === 0) {
      return (
        <div className="text-center py-8">
          <div className="text-gray-400 mb-2 text-2xl">🔧</div>
          <p className="text-gray-500 dark:text-gray-400">No tools were called during this step</p>
          <p className="text-sm text-gray-400 mt-2">
            Tool calls will appear here when an agent uses tools during execution
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">
          {currentStepToolCalls.length} tool call{currentStepToolCalls.length !== 1 ? 's' : ''} during this step
        </div>
        {currentStepToolCalls.map((tc, idx) => (
          <div
            key={tc.id}
            className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
          >
            {/* Tool call header */}
            <div className="flex items-center justify-between px-4 py-2 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-400">#{idx + 1}</span>
                <span className="font-semibold text-sm text-gray-800 dark:text-warm-white">{tc.name}</span>
              </div>
              {tc.timestamp && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {new Date(tc.timestamp).toLocaleTimeString()}
                </span>
              )}
            </div>

            <div className="p-4 space-y-3">
              {/* Arguments */}
              <div>
                <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                  Arguments
                </div>
                {Object.keys(tc.arguments).length === 0 ? (
                  <span className="text-xs text-gray-400 italic">No arguments</span>
                ) : (
                  <pre className="text-xs bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-2 overflow-x-auto text-gray-700 dark:text-gray-300">
                    {JSON.stringify(tc.arguments, null, 2)}
                  </pre>
                )}
              </div>

              {/* Result */}
              {tc.result && (
                <div>
                  <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                    Result
                  </div>
                  <pre className="text-xs bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-2 overflow-x-auto text-gray-700 dark:text-gray-300 max-h-60 overflow-y-auto">
                    {(() => {
                      try {
                        return JSON.stringify(JSON.parse(tc.result), null, 2);
                      } catch {
                        return tc.result;
                      }
                    })()}
                  </pre>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderMetadataContent = () => {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-warm-white mb-3 sm:mb-4">Step Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
            <div className="bg-white dark:bg-cap-dark border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Step Order:</span>
                  <span className="text-sm font-semibold">{runResult.step_order || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Status:</span>
                  <span className={`text-sm font-semibold px-2 py-1 rounded-full ${
                    runResult.status === 'completed'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                      : runResult.status === 'failed'
                      ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                      : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                  }`}>
                    {runResult.status || 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Duration:</span>
                  <span className="text-sm font-semibold">{formatDuration()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Session ID:</span>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-semibold">
                      {runResult.session_id || 'None'}
                    </span>
                    {runResult.session_id && (
                      <Tooltip content="View full session details">
                      <button
                        onClick={() => setActiveTab('session')}
                        className="text-xs text-purple-600 hover:text-purple-800 underline flex items-center"
                      >
                        View 💬
                      </button>
                      </Tooltip>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white dark:bg-cap-dark border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Step Type:</span>
                  <span className="text-sm font-semibold">{stepTypeName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Input Type:</span>
                  <span className="text-sm font-semibold">{runResult.input_type || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Input Step ID:</span>
                  <span className="text-sm font-semibold">{runResult.input_step_id || 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {runResult.error_message && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white mb-4">Error Information</h3>
            <RunResultErrorPanel runResult={runResult} onEditAgent={onEditAgent} />
          </div>
        )}
        
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white mb-4">Raw Data</h3>
          <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <pre className="text-xs text-gray-700 dark:text-gray-300 overflow-x-auto">
              {JSON.stringify(runResult, null, 2)}
            </pre>
          </div>
        </div>
      </div>
    );
  };

  const renderInstructionsContent = () => {
    const actualInstructions = runResult.hint_application_details?.actual_instructions;
    
    if (!actualInstructions) {
      return (
        <div className="text-center py-8">
          <div className="text-gray-400 mb-2">📋</div>
          <p className="text-gray-500 dark:text-gray-400">No instruction data available for this step</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white mb-4">Agent Instructions</h3>
          <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <pre className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
              {actualInstructions}
            </pre>
          </div>
        </div>
        
        {runResult.hint_application_details?.applied_hints &&
         runResult.hint_application_details.applied_hints.length > 0 && (
          <div className="bg-primary-50 dark:bg-blue-900/20 border border-primary-200 dark:border-blue-800 rounded-lg p-4">
            <h4 className="text-md font-semibold text-blue-900 dark:text-blue-200 mb-2">
              Note: These instructions include feedback loop enhancements
            </h4>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              The instructions above show the complete text that was sent to the agent,
              including any modifications made based on feedback loop hints.
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderHintsContent = () => {
    if (!runResult.hint_application_details) {
      return (
        <div className="text-center py-8">
          <div className="text-gray-400 mb-2">💡</div>
          <p className="text-gray-500 dark:text-gray-400">No hints were applied to this step</p>
        </div>
      );
    }

    const { iteration, previous_feedback, previous_score, applied_hints } = runResult.hint_application_details;

    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white mb-4">Hint Application Details</h3>
          
          {/* Feedback Loop Context */}
          <div className="bg-primary-50 dark:bg-blue-900/20 border border-primary-200 dark:border-blue-800 rounded-lg p-4 mb-6">
            <h4 className="text-md font-semibold text-blue-900 dark:text-blue-200 mb-3">Feedback Loop Context</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {iteration && (
                <div>
                  <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Iteration:</span>
                  <span className="text-sm font-semibold text-blue-900 dark:text-blue-200 ml-2">{iteration}</span>
                </div>
              )}
              {previous_score !== undefined && previous_score !== null && (
                <div>
                  <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Previous Score:</span>
                  <span className="text-sm font-semibold text-blue-900 dark:text-blue-200 ml-2">
                    {typeof previous_score === 'boolean' ? previous_score.toString() : previous_score}
                  </span>
                </div>
              )}
              {previous_feedback && (
                <div className="md:col-span-2">
                  <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Previous Feedback:</span>
                  <div className="mt-1 p-2 bg-white dark:bg-cap-dark border border-primary-200 dark:border-blue-800 rounded text-sm text-blue-900 dark:text-blue-200">
                    {previous_feedback}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Applied Hints */}
          {applied_hints && applied_hints.length > 0 && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <h4 className="text-md font-semibold text-green-900 dark:text-green-200 mb-3">Applied Hints</h4>
              <div className="space-y-3">
                {applied_hints.map((hint, index) => (
                  <div key={index} className="bg-white dark:bg-cap-dark border border-green-200 dark:border-green-800 rounded p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-green-800 dark:text-green-300 capitalize">
                          {hint.hint_type.replace(/_/g, ' ')}
                        </div>
                        <div className="text-sm text-green-700 dark:text-green-400 mt-1">
                          {hint.value}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSessionContent = () => {
    const currentSessionId = runResult?.session_id;

    // Case 1: No session_id - step doesn't use sessions
    if (!currentSessionId) {
      return (
        <div className="text-center py-8">
          <div className="text-gray-400 mb-2">💬</div>
          <p className="text-gray-500 dark:text-gray-400">This step does not use session persistence</p>
          <p className="text-sm text-gray-400 mt-2">
            Session persistence is configured at the workflow step level
          </p>
        </div>
      );
    }

    const currentSessionInfo = sessionInfoMap?.[currentSessionId];
    const currentMessages = sessionMessages?.filter(m => m.session_id === currentSessionId) || [];

    // Case 2: Has session_id but session not found (expired/deleted)
    if (!currentSessionInfo) {
      return (
        <div className="text-center py-8">
          <div className="text-yellow-400 mb-2">⚠️</div>
          <p className="text-gray-700 dark:text-gray-300 font-medium">Session Not Found</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            This step referenced session ID {currentSessionId}, but the session
            may have expired or been deleted.
          </p>
        </div>
      );
    }

    // Case 3: Session exists but has no messages yet
    if (currentMessages.length === 0) {
      return (
        <div className="text-center py-8">
          <div className="text-gray-400 mb-2">💬</div>
          <p className="text-gray-500 dark:text-gray-400">Session is active but has no messages yet</p>
        </div>
      );
    }

    // Case 4: Normal case - display session info and messages
    return (
      <div className="space-y-6">
        {/* Session Metadata Card */}
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white mb-3 flex items-center">
            <span className="mr-2">🔑</span>
            Session Information
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
            <div className="bg-white dark:bg-cap-dark rounded-lg p-3 border border-purple-100 dark:border-purple-800">
              <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Session Key</p>
              <Tooltip content={currentSessionInfo.session_key}>
              <p className="text-sm font-mono font-semibold text-gray-900 dark:text-warm-white truncate">
                {currentSessionInfo.session_key}
              </p>
              </Tooltip>
            </div>
            <div className="bg-white dark:bg-cap-dark rounded-lg p-3 border border-purple-100 dark:border-purple-800">
              <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Type</p>
              <p className="text-sm font-semibold text-gray-900 dark:text-warm-white capitalize">
                {currentSessionInfo.session_type}
              </p>
            </div>
            <div className="bg-white dark:bg-cap-dark rounded-lg p-3 border border-purple-100 dark:border-purple-800">
              <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Messages</p>
              <p className="text-sm font-semibold text-gray-900 dark:text-warm-white">
                {currentMessages.length}
              </p>
            </div>
            <div className="bg-white dark:bg-cap-dark rounded-lg p-3 border border-purple-100 dark:border-purple-800">
              <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Status</p>
              <span className={`text-sm font-semibold px-2 py-1 rounded-full ${
                currentSessionInfo.is_active
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                  : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
              }`}>
                {currentSessionInfo.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div className="bg-white dark:bg-cap-dark rounded-lg p-3 border border-purple-100 dark:border-purple-800">
              <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Created</p>
              <p className="text-xs font-semibold text-gray-900 dark:text-warm-white">
                {new Date(currentSessionInfo.created_at).toLocaleString()}
              </p>
            </div>
            {currentSessionInfo.expires_at && (
              <div className="bg-white dark:bg-cap-dark rounded-lg p-3 border border-purple-100 dark:border-purple-800">
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Expires</p>
                <p className="text-xs font-semibold text-gray-900 dark:text-warm-white">
                  {new Date(currentSessionInfo.expires_at).toLocaleString()}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Conversation History */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white mb-4 flex items-center">
            <span className="mr-2">💬</span>
            Conversation History
            <span className="ml-2 text-sm text-gray-500 dark:text-gray-400 font-normal">
              ({currentMessages.length} messages)
            </span>
          </h3>

          <div className="space-y-3 max-h-96 overflow-y-auto">
            {currentMessages.map((msg, index) => (
              <div
                key={msg.message_id || index}
                className={`rounded-lg p-4 border ${
                  msg.role === 'user'
                    ? 'bg-primary-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 ml-8'
                    : msg.role === 'assistant'
                    ? 'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700 mr-8'
                    : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                }`}
              >
                {/* Message Header */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                      {msg.role === 'user' ? '👤 User' :
                       msg.role === 'assistant' ? '🤖 Assistant' :
                       msg.role === 'system' ? '⚙️ System' : '🔧 Tool'}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      #{msg.message_order + 1}
                    </span>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {new Date(msg.created_at).toLocaleTimeString()}
                  </span>
                </div>

                {/* Message Content */}
                <div className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                  {msg.content}
                </div>

                {/* Tool Calls (if present) */}
                {msg.tool_calls && msg.tool_calls.length > 0 && (
                  <div className="mt-2 p-2 bg-white dark:bg-cap-dark border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-warm-white text-xs">
                    <span className="font-semibold">🔧 Tool Calls:</span>
                    <pre className="mt-1 text-gray-600 dark:text-gray-400 overflow-x-auto">
                      {JSON.stringify(msg.tool_calls, null, 2)}
                    </pre>
                  </div>
                )}

                {/* Metadata (if present) */}
                {msg.message_metadata && Object.keys(msg.message_metadata).length > 0 && (
                  <details className="mt-2">
                    <summary className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:text-gray-300">
                      View Metadata
                    </summary>
                    <pre className="mt-1 p-2 bg-white dark:bg-cap-dark border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-warm-white text-xs text-gray-600 dark:text-gray-400 overflow-x-auto">
                      {JSON.stringify(msg.message_metadata, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Session Actions */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Session data is automatically managed based on step configuration
          </div>
          <button
            onClick={() => {
              if (!currentSessionInfo) {
                alert('No session data available to export');
                return;
              }

              const sessionData = {
                session_info: currentSessionInfo,
                messages: currentMessages,
                exported_at: new Date().toISOString()
              };
              const blob = new Blob([JSON.stringify(sessionData, null, 2)], { type: 'application/json' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `session-${currentSessionInfo.session_key}-${Date.now()}.json`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
            }}
            className="px-3 py-1 text-sm bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-900/50 rounded-md transition-colors border border-purple-300 dark:border-purple-700"
          >
            Export Session
          </button>
        </div>
      </div>
    );
  };

  // AC-388: Registry tab content
  const renderRegistryContent = () => {
    if (registryLoading) {
      return (
        <div className="text-center py-8">
          <div className="animate-pulse text-gray-500 dark:text-gray-400">Loading registry entries...</div>
        </div>
      );
    }

    if (registryEntries.length === 0) {
      return (
        <div className="text-center py-8">
          <Archive className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500 dark:text-gray-400">No registry entries were created during this run</p>
          <p className="text-sm text-gray-400 mt-2">
            Registry entries are created when agents use registry tools
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {/* Summary */}
        <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Archive className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            <h3 className="font-semibold text-purple-900 dark:text-purple-200">Registry Activity</h3>
          </div>
          <p className="text-sm text-purple-700 dark:text-purple-300">
            {registryTotal} {registryTotal === 1 ? 'entry' : 'entries'} created during this run
          </p>
        </div>

        {/* Entries list */}
        <div className="space-y-3">
          {registryEntries.map((entry) => (
            <div key={entry.id} className="bg-white dark:bg-cap-dark border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Model ID: {entry.model_id} • Created: {new Date(entry.created_at).toLocaleString()}
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900 rounded p-3 overflow-x-auto">
                <pre className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                  {JSON.stringify(entry.output_value, null, 2)}
                </pre>
              </div>
              {entry.dedup_key_value && (
                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  <span className="font-medium">Dedup Key:</span>{' '}
                  <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">
                    {JSON.stringify(entry.dedup_key_value)}
                  </code>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white dark:bg-cap-dark rounded-lg shadow-xl max-w-6xl w-full max-h-[98vh] sm:max-h-[95vh] overflow-hidden">
        {/* Modal Header */}
        <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          {/* Top row: title + close/export */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
              <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                runResult.status === 'completed'
                  ? 'bg-green-500'
                  : runResult.status === 'failed'
                  ? 'bg-red-500'
                  : 'bg-yellow-500'
              }`}>
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {runResult.status === 'completed' ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  ) : runResult.status === 'failed' ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  )}
                </svg>
              </div>
              <div className="min-w-0">
                <h2 className="text-base sm:text-xl font-bold text-gray-900 dark:text-warm-white truncate">{title}</h2>
                <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap text-sm text-gray-600 dark:text-gray-400">
                  <span className="font-semibold text-gray-900 dark:text-warm-white bg-gray-100 dark:bg-gray-800 px-1.5 sm:px-2 py-0.5 rounded text-xs sm:text-sm">
                    Step {runResult.step_order}
                  </span>
                  <div className="flex items-center gap-1.5">
                    <StepIcon className={`w-4 h-4 ${stepTypeColor}`} />
                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 px-1.5 sm:px-2 py-0.5 bg-white dark:bg-cap-dark rounded-md border border-gray-200 dark:border-gray-700">
                      {stepTypeName}
                    </span>
                  </div>
                  {queueDisplayName && (
                    <span className={`text-xs font-medium px-1.5 sm:px-2 py-0.5 rounded-md border ${
                      currentStep?.step_type === 'queue_write'
                        ? 'text-teal-700 dark:text-teal-300 bg-teal-50 dark:bg-teal-900/30 border-teal-200 dark:border-teal-800'
                        : 'text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/30 border-purple-200 dark:border-purple-800'
                    }`}>
                      {queueDisplayName}
                    </span>
                  )}
                  {agent && (
                    <span className="text-xs font-medium text-indigo-600 dark:text-indigo-300 px-1.5 sm:px-2 py-0.5 bg-indigo-50 dark:bg-indigo-900/30 rounded-md border border-indigo-200 dark:border-indigo-800">
                      {agent.name}
                    </span>
                  )}
                  {currentStep?.label && currentStep.label !== agent?.name && currentStep.label !== queueDisplayName && (
                    <>
                      <span className="text-xs text-gray-500 dark:text-gray-400 hidden sm:inline">•</span>
                      <span className="text-xs text-gray-600 dark:text-gray-400 italic hidden sm:inline">
                        "{currentStep.label}"
                      </span>
                    </>
                  )}
                  <span className="hidden sm:inline">•</span>
                  <span className={`px-1.5 sm:px-2 py-0.5 rounded text-xs font-medium ${
                    runResult.status === 'completed'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 border border-green-300'
                      : runResult.status === 'failed'
                      ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 border border-red-300'
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600'
                  }`}>
                    {runResult.status}
                  </span>
                  <span className="hidden sm:inline">•</span>
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300 hidden sm:inline">{formatDuration()}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0">
              <button
                onClick={exportOutput}
                className="px-2 sm:px-3 py-1 text-xs sm:text-sm bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-900/50 rounded-md transition-colors border border-purple-300 dark:border-purple-700"
              >
                Export
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-400 transition-colors p-1"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Navigation Controls - separate row on mobile */}
          <div className="flex items-center justify-center sm:justify-start mt-2 space-x-2">
            <button
              onClick={() => previousStep && handleNavigateToStep(previousStep)}
              disabled={!previousStep}
              className={`flex items-center space-x-1 px-2 sm:px-3 py-1 text-xs sm:text-sm rounded-md transition-colors border ${
                previousStep
                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50 border-blue-300 dark:border-blue-700'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-400 border-gray-300 dark:border-gray-600 cursor-not-allowed'
              }`}
            >
              <Tooltip content={previousStep ? `Go to Step ${previousStep.step_order} (←)` : 'No previous step'}>
              <ChevronLeft className="w-4 h-4" />
              </Tooltip>
              <span className="hidden sm:inline">Previous</span>
            </button>

            <Tooltip content="Use arrow keys to navigate">
            <div className="flex items-center space-x-1 px-2 py-1 text-xs text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 rounded border dark:border-gray-600">
              <span>{runResult.step_order}</span>
              <span>/</span>
              <span>{allRunResults.length}</span>
            </div>
            </Tooltip>

            <button
              onClick={() => nextStep && handleNavigateToStep(nextStep)}
              disabled={!nextStep}
              className={`flex items-center space-x-1 px-2 sm:px-3 py-1 text-xs sm:text-sm rounded-md transition-colors border ${
                nextStep
                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50 border-blue-300 dark:border-blue-700'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-400 border-gray-300 dark:border-gray-600 cursor-not-allowed'
              }`}
            >
              <Tooltip content={nextStep ? `Go to Step ${nextStep.step_order} (→)` : 'No next step'}>
              <span className="hidden sm:inline">Next</span>
              </Tooltip>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-cap-dark">
          <div className="flex overflow-x-auto scrollbar-hide space-x-2 sm:space-x-6 md:space-x-8 px-3 sm:px-6">
            {[
              { key: 'output', label: 'Output', icon: '📄' },
              { key: 'input', label: 'Input', icon: '📥' },
              { key: 'tools', label: 'Tool Usage', icon: '🔧', badge: currentStepToolCalls.length > 0 ? currentStepToolCalls.length : undefined },
              { key: 'metadata', label: 'Metadata', icon: 'ℹ️' },
              { key: 'hints', label: 'Hints', icon: '💡' },
              { key: 'instructions', label: 'Instructions', icon: '📋' },
              { key: 'session', label: 'Session', icon: '💬' },
              { key: 'registry', label: 'Registry', icon: '📦', badge: registryTotal > 0 ? registryTotal : undefined }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as any)}
                className={`py-3 px-1 border-b-2 font-medium text-xs sm:text-sm transition-colors flex items-center whitespace-nowrap flex-shrink-0 ${
                  activeTab === tab.key
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600'
                }`}
              >
                <span className="mr-1 sm:mr-2">{tab.icon}</span>
                {tab.label}
                {(tab as any).badge && (
                  <span className={`ml-1 sm:ml-2 px-1.5 py-0.5 text-xs rounded-full ${
                    activeTab === tab.key
                      ? 'bg-blue-100 text-primary-600'
                      : 'bg-purple-100 text-purple-600'
                  }`}>
                    {(tab as any).badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Modal Body */}
        <div className="p-3 sm:p-6 overflow-y-auto max-h-[calc(98vh-220px)] sm:max-h-[calc(90vh-180px)]">
          {activeTab === 'output' && renderOutputContent()}
          {activeTab === 'input' && renderInputContent()}
          {activeTab === 'tools' && renderToolUsageContent()}
          {activeTab === 'metadata' && renderMetadataContent()}
          {activeTab === 'hints' && renderHintsContent()}
          {activeTab === 'instructions' && renderInstructionsContent()}
          {activeTab === 'session' && renderSessionContent()}
          {activeTab === 'registry' && renderRegistryContent()}
        </div>
      </div>
    </div>
  );
};

export default OutputDisplayModal; 